/**
 * Admin Dashboard Editor - Full Page View
 * 
 * A full-screen dashboard editor where admins can:
 * - Drag sections to reorder them
 * - Resize sections by dragging corners
 * - Edit welcome banner content
 * - Save changes globally to all users
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import GridLayout from 'react-grid-layout';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  GripHorizontal, Save, X, ArrowLeft, Eye, Edit3, 
  Loader2, Check, AlertCircle, Settings2, Users
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription 
} from '../components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

// Import section components
import DashboardCalendarWidget from '../components/DashboardCalendarWidget';
import WhatsComingOnlinePanel from '../components/WhatsComingOnlinePanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section registry
const SECTION_REGISTRY = {
  welcome_banner: {
    title: 'Welcome Banner',
    minW: 6, minH: 2, maxH: 3
  },
  key_metrics: {
    title: 'Key Metrics',
    minW: 6, minH: 2, maxH: 4
  },
  upcoming_events: {
    title: 'Upcoming Events',
    minW: 3, minH: 3, maxH: 6
  },
  recent_activity: {
    title: 'Recent Activity',
    minW: 3, minH: 3, maxH: 6
  },
  whats_coming_online: {
    title: "What's Coming Online",
    minW: 3, minH: 3, maxH: 6
  }
};

// ==================== SECTION COMPONENTS ====================

function WelcomeBannerSection({ welcomeConfig }) {
  return (
    <div className="h-full bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg p-6 flex flex-col justify-center">
      <h3 className="text-2xl font-bold mb-2">{welcomeConfig?.title || 'Welcome to RhinoShield Platform'}</h3>
      <p className="text-blue-100">
        {welcomeConfig?.description || 'Your mission control center for managing operations, tracking performance, and driving results.'}
      </p>
    </div>
  );
}

function KeyMetricsSection() {
  const kpis = [
    { label: 'My Jobs', value: '0' },
    { label: 'In Progress', value: '0' },
    { label: 'Completed', value: '0' },
    { label: 'Completion Rate', value: '0%' }
  ];
  
  return (
    <div className="h-full grid grid-cols-2 lg:grid-cols-4 gap-3 p-2">
      {kpis.map((kpi, i) => (
        <div key={i} className="bg-accent/50 rounded-lg p-3 flex flex-col justify-center">
          <p className="text-xs text-muted-foreground">{kpi.label}</p>
          <p className="text-xl font-bold">{kpi.value}</p>
        </div>
      ))}
    </div>
  );
}

function UpcomingEventsSection() {
  return (
    <div className="h-full overflow-hidden">
      <DashboardCalendarWidget embedded />
    </div>
  );
}

function RecentActivitySection() {
  const activities = [
    { message: 'You logged in', time: 'Just now' }
  ];
  
  return (
    <div className="h-full overflow-auto p-2 space-y-2">
      {activities.map((activity, i) => (
        <div key={i} className="flex items-start gap-2 p-2 rounded-lg hover:bg-accent/50 transition-colors">
          <div className="w-2 h-2 rounded-full bg-primary mt-2" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{activity.message}</p>
            <p className="text-xs text-muted-foreground">{activity.time}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function WhatsComingOnlineSection() {
  return (
    <div className="h-full overflow-hidden">
      <WhatsComingOnlinePanel embedded />
    </div>
  );
}

// Component map
const SECTION_COMPONENTS = {
  welcome_banner: WelcomeBannerSection,
  key_metrics: KeyMetricsSection,
  upcoming_events: UpcomingEventsSection,
  recent_activity: RecentActivitySection,
  whats_coming_online: WhatsComingOnlineSection
};

// ==================== SECTION WRAPPER ====================

function EditorSection({ sectionId, title, welcomeConfig, onDelete }) {
  const SectionComponent = SECTION_COMPONENTS[sectionId];
  
  const handleDelete = (e) => {
    e.stopPropagation();
    e.preventDefault();
    e.nativeEvent.stopImmediatePropagation();
    onDelete();
  };
  
  if (!SectionComponent) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        Unknown section: {sectionId}
      </div>
    );
  }
  
  return (
    <Card className="h-full flex flex-col overflow-hidden border-2 border-dashed border-blue-300 dark:border-blue-700 hover:border-blue-500 transition-colors">
      {/* Section Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-blue-50 dark:bg-blue-900/30">
        {/* Drag Handle Area */}
        <div className="section-drag-handle flex items-center gap-2 flex-1 cursor-grab active:cursor-grabbing">
          <GripHorizontal className="h-4 w-4 text-blue-500" />
          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">{title}</span>
          <Badge variant="outline" className="text-xs text-blue-600 border-blue-300 ml-2">
            Drag to move
          </Badge>
        </div>
        {/* Delete Button - Outside drag handle */}
        <Button
          variant="ghost"
          size="icon"
          onMouseDown={(e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}
          onClick={handleDelete}
          className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 ml-2"
          title="Remove section"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Section Content */}
      <div className="flex-1 overflow-hidden">
        <SectionComponent welcomeConfig={welcomeConfig} />
      </div>
    </Card>
  );
}

// ==================== MAIN EDITOR PAGE ====================

export default function AdminDashboardEditor() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1200);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [layout, setLayout] = useState([]);
  const [originalLayout, setOriginalLayout] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  
  // Welcome config
  const [welcomeConfig, setWelcomeConfig] = useState({
    title: 'Welcome to RhinoShield Platform',
    description: 'Your mission control center for managing operations, tracking performance, and driving results.'
  });
  const [welcomeSheetOpen, setWelcomeSheetOpen] = useState(false);
  
  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, sectionId: null, sectionName: '' });
  
  // Grid config
  const GRID_COLS = 12;
  const ROW_HEIGHT = 80;
  
  // Measure container width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    
    updateWidth();
    const timeoutId = setTimeout(updateWidth, 100);
    window.addEventListener('resize', updateWidth);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateWidth);
    };
  }, []);
  
  // Fetch system default dashboard
  const fetchData = useCallback(async () => {
    try {
      const [dashboardRes, welcomeRes] = await Promise.all([
        axios.get(`${API}/dashboards/admin/system-default`),
        axios.get(`${API}/platform/welcome-config`)
      ]);
      
      const dashboardLayout = dashboardRes.data.layout || [];
      
      // Convert to grid layout format
      const gridLayout = dashboardLayout.map(item => ({
        i: item.section_id,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
        minW: SECTION_REGISTRY[item.section_id]?.minW || 3,
        minH: 2,
        maxH: SECTION_REGISTRY[item.section_id]?.maxH || 6
      }));
      
      setLayout(gridLayout);
      setOriginalLayout(JSON.stringify(dashboardLayout));
      setWelcomeConfig(welcomeRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Handle layout change
  const handleLayoutChange = useCallback((newLayout) => {
    setLayout(newLayout);
    
    // Check if dirty
    const currentLayoutData = newLayout
      .map(item => ({
        section_id: item.i,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
        minimized: false
      }))
      .sort((a, b) => a.section_id.localeCompare(b.section_id));
    
    const originalLayoutData = originalLayout 
      ? JSON.parse(originalLayout).sort((a, b) => a.section_id.localeCompare(b.section_id))
      : [];
    
    setIsDirty(JSON.stringify(currentLayoutData) !== JSON.stringify(originalLayoutData));
  }, [originalLayout]);
  
  // Open delete confirmation dialog
  const handleDeleteSection = (sectionId) => {
    const sectionName = SECTION_REGISTRY[sectionId]?.title || sectionId;
    setDeleteDialog({ open: true, sectionId, sectionName });
  };
  
  // Confirm deletion
  const confirmDelete = () => {
    const { sectionId, sectionName } = deleteDialog;
    setLayout(prev => prev.filter(item => item.i !== sectionId));
    setIsDirty(true);
    toast.success(`"${sectionName}" removed from dashboard`);
    setDeleteDialog({ open: false, sectionId: null, sectionName: '' });
  };
  
  // Cancel deletion
  const cancelDelete = () => {
    setDeleteDialog({ open: false, sectionId: null, sectionName: '' });
  };
  
  // Save globally
  const handleSaveGlobally = async () => {
    setSaving(true);
    try {
      const layoutData = layout.map(item => ({
        section_id: item.i,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
        minimized: false
      }));
      
      await Promise.all([
        axios.put(`${API}/dashboards/admin/system-default`, { layout: layoutData }),
        axios.put(`${API}/platform/welcome-config`, welcomeConfig)
      ]);
      
      setOriginalLayout(JSON.stringify(layoutData));
      setIsDirty(false);
      toast.success('Dashboard saved globally!');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save dashboard');
    } finally {
      setSaving(false);
    }
  };
  
  // Push to all users
  const handlePushToAllUsers = async () => {
    if (!window.confirm('This will reset ALL users to the system default dashboard. Users will lose their personal dashboard customizations. Continue?')) {
      return;
    }
    
    setSaving(true);
    try {
      // First save current layout
      const layoutData = layout.map(item => ({
        section_id: item.i,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
        minimized: false
      }));
      
      await Promise.all([
        axios.put(`${API}/dashboards/admin/system-default`, { layout: layoutData }),
        axios.put(`${API}/platform/welcome-config`, welcomeConfig)
      ]);
      
      // Then push to all users
      const res = await axios.post(`${API}/dashboards/admin/push-to-all-users`);
      
      setOriginalLayout(JSON.stringify(layoutData));
      setIsDirty(false);
      toast.success(`Dashboard pushed to all users! ${res.data.deleted_count} personal dashboards reset.`);
    } catch (error) {
      console.error('Failed to push:', error);
      toast.error('Failed to push to all users');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle exit
  const handleExit = () => {
    if (isDirty) {
      if (window.confirm('You have unsaved changes. Are you sure you want to exit?')) {
        navigate('/admin');
      }
    } else {
      navigate('/admin');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-muted-foreground">Loading dashboard editor...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900">
      {/* Floating Header Toolbar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-slate-800 border-b shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleExit}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Exit Editor
            </Button>
            
            <div className="h-6 w-px bg-border" />
            
            <div className="flex items-center gap-2">
              <Edit3 className="h-4 w-4 text-blue-500" />
              <span className="font-semibold">Default Dashboard Editor</span>
              <Badge variant="outline" className="text-blue-600 border-blue-300">
                Global
              </Badge>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {isDirty && (
              <Badge variant="outline" className="text-amber-600 border-amber-400">
                <AlertCircle className="h-3 w-3 mr-1" />
                Unsaved changes
              </Badge>
            )}
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setWelcomeSheetOpen(true)}
            >
              <Settings2 className="h-4 w-4 mr-2" />
              Edit Welcome
            </Button>
            
            <Button 
              size="sm"
              onClick={handleSaveGlobally}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Globally
            </Button>
            
            <Button 
              size="sm"
              variant="destructive"
              onClick={handlePushToAllUsers}
              disabled={saving}
            >
              <Users className="h-4 w-4 mr-2" />
              Push to All Users
            </Button>
          </div>
        </div>
        
        {/* Instructions bar */}
        <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/30 border-t text-sm text-blue-700 dark:text-blue-300">
          <div className="flex items-center gap-4">
            <span>💡 <strong>Drag</strong> sections by their header to reorder</span>
            <span className="text-blue-400">|</span>
            <span><strong>Resize</strong> from the bottom-right corner</span>
            <span className="text-blue-400">|</span>
            <span>Changes apply to <strong>all users</strong> when saved</span>
          </div>
        </div>
      </div>
      
      {/* Main Editor Area */}
      <div className="pt-28 pb-8 px-4" ref={containerRef}>
        <div className="max-w-[1600px] mx-auto">
          <GridLayout
            className="layout"
            layout={layout}
            cols={GRID_COLS}
            rowHeight={ROW_HEIGHT}
            width={Math.min(containerWidth - 32, 1568)}
            onLayoutChange={handleLayoutChange}
            draggableHandle=".section-drag-handle"
            compactType="vertical"
            preventCollision={false}
            isResizable={true}
            resizeHandles={['se']}
            margin={[16, 16]}
          >
            {layout.map(item => (
              <div key={item.i} data-grid={item}>
                <EditorSection
                  sectionId={item.i}
                  title={SECTION_REGISTRY[item.i]?.title || item.i}
                  welcomeConfig={welcomeConfig}
                  onDelete={() => handleDeleteSection(item.i)}
                />
              </div>
            ))}
          </GridLayout>
        </div>
      </div>
      
      {/* Welcome Banner Edit Sheet */}
      <Sheet open={welcomeSheetOpen} onOpenChange={setWelcomeSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Edit Welcome Banner</SheetTitle>
            <SheetDescription>
              Customize the welcome message shown to all users
            </SheetDescription>
          </SheetHeader>
          
          <div className="space-y-4 mt-6">
            <div>
              <Label htmlFor="welcome-title">Title</Label>
              <Input
                id="welcome-title"
                value={welcomeConfig.title}
                onChange={(e) => {
                  setWelcomeConfig({ ...welcomeConfig, title: e.target.value });
                  setIsDirty(true);
                }}
                placeholder="Welcome to RhinoShield Platform"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="welcome-desc">Description</Label>
              <Textarea
                id="welcome-desc"
                value={welcomeConfig.description}
                onChange={(e) => {
                  setWelcomeConfig({ ...welcomeConfig, description: e.target.value });
                  setIsDirty(true);
                }}
                placeholder="Your mission control center..."
                className="mt-1"
                rows={4}
              />
            </div>
            
            {/* Preview */}
            <div className="mt-6">
              <Label className="text-xs text-muted-foreground">Preview</Label>
              <div className="mt-2 p-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg">
                <h3 className="text-lg font-bold">{welcomeConfig.title || 'Welcome'}</h3>
                <p className="text-sm text-blue-100 mt-1">{welcomeConfig.description || 'Add a description...'}</p>
              </div>
            </div>
            
            <Button 
              className="w-full mt-4" 
              onClick={() => setWelcomeSheetOpen(false)}
            >
              <Check className="h-4 w-4 mr-2" />
              Done
            </Button>
          </div>
        </SheetContent>
      </Sheet>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={cancelDelete}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Section?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>&quot;{deleteDialog.sectionName}&quot;</strong> from the dashboard?
              <br /><br />
              This section will be removed for all users when you save. You can add it back by resetting to default.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Remove Section
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
